import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQueryParams } from 'use-query-params';

import { useAuth } from 'contexts/auth';
import { APP_V1_BASEURL } from 'lib/urls';
import store from 'store';

import type { CompanyLinkDefaultType } from 'contexts/auth/auth.types';
import type { RootState } from 'store/store.types';

const selectCurrentProfileRival = store.select(({ profiles }) => ({
  rival: profiles.currentProfileRival,
}));

export const useRedirect = () => {
  const { companyLinkDefault, isCurator, isNewCardEditorEnabled } = useAuth();
  const navigate = useNavigate();
  const [, setQuery] = useQueryParams();
  const currentProfileId = useSelector<RootState, number | undefined>(
    (state) => state.profiles.currentProfileId,
  );
  const { rival } = useSelector(selectCurrentProfileRival);

  const getRivalRedirectLink = (
    profileId?: number,
    defaultLink: CompanyLinkDefaultType = companyLinkDefault,
    supportedRivalId: number | undefined = rival?.supportedRivalId,
  ) => {
    if (defaultLink === 'landing_page' && supportedRivalId) {
      return `/company/${supportedRivalId}/profile/${profileId}`;
    } else if (defaultLink === 'battlecard') {
      return `/profile/${profileId}/battlecard/view`;
    }
    return `/profile/${profileId}`;
  };

  const redirectToV1 = () => {
    return window.open(
      `${APP_V1_BASEURL}${window.location.pathname}${window.location.search}`,
      '_self',
    );
  };

  const redirectToProfile = (profileId?: number, boardId?: number) => {
    const id = profileId ?? currentProfileId;

    if (isCurator && !isNewCardEditorEnabled) {
      redirectToV1Profile(id);
    }

    return void navigate(`/profile/${id}${boardId ? `?lanes=${boardId}` : ''}`);
  };

  const redirectToBattlecards = (profileId?: number) => {
    const id = profileId ?? currentProfileId;
    if (isCurator) {
      return window.open(
        `${APP_V1_BASEURL}/profile/${id}/battlecard/view`,
        '_self',
      );
    }

    return void navigate(`/profile/${profileId}/battlecard/view`);
  };

  const redirectToLandingPage = (
    profileId?: number,
    supportedRivalId?: number,
  ) => {
    const srId = supportedRivalId || rival?.supportedRivalId;
    const pId = profileId || currentProfileId;
    const srRoute = `/company/${srId}`;
    const profileRoute = pId ? `/profile/${pId}` : '';
    return srId
      ? void navigate(`${srRoute}${profileRoute}`)
      : redirectToBattlecards(profileId);
  };

  const redirectToCompany = (profileId?: number, supportedRivalId?: number) => {
    if (companyLinkDefault === 'landing_page') {
      redirectToLandingPage(profileId, supportedRivalId);
    } else if (companyLinkDefault === 'battlecard') {
      redirectToBattlecards(profileId);
    } else {
      redirectToProfile(profileId);
    }
  };

  const redirectToLaneCardWithHighlight = (
    {
      profileId,
      laneId,
      cardId,
    }: {
      profileId: number;
      laneId: number;
      cardId: number;
    },
    options?: { target?: string },
  ) => {
    const { target = '_self' } = options ?? {};
    return window.open(
      `${APP_V1_BASEURL}/profile/${profileId}/edit#${laneId}/${cardId}`,
      target,
    );
  };

  const redirectToCard = ({
    profileId,
    cardId,
  }: {
    profileId: number;
    cardId: number;
  }) => {
    return window.open(
      `${APP_V1_BASEURL}/profile/${profileId}/edit/card/${cardId}`,
    );
  };

  const redirectToCardEditor = ({
    profileId,
    cardId,
  }: {
    profileId?: number;
    cardId: number;
  }) => {
    if (isNewCardEditorEnabled) {
      return setQuery({ editCard: cardId, viewCard: undefined });
    }

    if (!profileId) {
      return;
    }

    const cardEditorPath = `/profile/${profileId}/edit/card/${cardId}/edit`;

    return window.open(`${APP_V1_BASEURL}${cardEditorPath}`);
  };

  const redirectToV1Profile = (profileId?: number) => {
    const id = profileId ?? currentProfileId;
    return window.open(`${APP_V1_BASEURL}/profile/${id}`, '_self');
  };

  const redirectToV1Search = (queryParams?: string) => {
    return window.open(`${APP_V1_BASEURL}/search${queryParams || ''}`, '_self');
  };

  const redirectToV1Settings = () =>
    window.open(`${APP_V1_BASEURL}/settings/alerts`, '_self');

  const redirectToV1Admin = () =>
    window.open(`${APP_V1_BASEURL}/admin`, '_self');

  const redirectToBattlecard = (
    profileId?: number,
    battlecardId?: number,
    options?: { target?: string },
  ) => {
    const { target = '_self' } = options ?? {};
    const id = profileId ?? currentProfileId;
    if (isCurator) {
      return window.open(
        `${APP_V1_BASEURL}/profile/${id}/battlecard/view/${battlecardId}`,
        target,
      );
    }

    return void navigate(`/profile/${id}/battlecard/view/${battlecardId}`);
  };

  const redirectToBattlecardsCard = (
    currentProfileId?: number,
    currentBattlecardId?: number,
    queryCardId?: number,
  ) => {
    if (isCurator) {
      return window.open(
        `${APP_V1_BASEURL}/profile/${currentProfileId}/battlecard/view/${currentBattlecardId}&cardId=${queryCardId}`,
        '_self',
      );
    }
    return void navigate(
      `/profile/${currentProfileId}/battlecard/view/${currentBattlecardId}?cardId=${queryCardId}`,
    );
  };

  const redirectToNews = (profileId?: number) => {
    return void navigate(`/profile/${profileId}/news`);
  };

  return {
    getRivalRedirectLink,
    redirectToProfile,
    redirectToBattlecards,
    redirectToLaneCardWithHighlight,
    redirectToCard,
    redirectToCardEditor,
    redirectToV1Profile,
    redirectToV1Search,
    redirectToV1,
    redirectToV1Settings,
    redirectToV1Admin,
    redirectToBattlecard,
    redirectToBattlecardsCard,
    redirectToLandingPage,
    redirectToNews,
    redirectToCompany,
  };
};
